<template>
    <div class="section">
        <Headbar>
            <template v-slot:left>
                <h1>{{ $t('sites.sites') }}</h1>
            </template>
            <template v-slot:right>
                <Button className="--primary --small" :onclick="toggleCreate" v-if="$store.getters.hasAnyPermission(['store sites'])">{{$t('sites.add_site')}}</Button>
            </template>
        </Headbar>

        <main>
            <vue-good-table
                mode="remote"
                styleClass="vgt-table vgt-custom"
                :columns="columns"
                :rows="sites"
                :isLoading.sync="is_loading_sites"
                :search-options="{
                    enabled: false,
                }"
                :pagination-options="{
                    enabled: true,
                    mode: 'records',
                    dropdownAllowAll: false,
                    perPage: 10,
                    perPageDropdownEnabled: true,
                    rowsPerPageLabel: $t('x_per_page', {x: $t('sites.sites')}),
                }"
                :sort-options="{
                  enabled: true,
                  multipleColumns: true,
                }"
                :totalRows="totalRecords"
                @on-page-change="onPageChange"
                @on-per-page-change="onPerPageChange"
                @on-sort-change="onSortChange">
                <template slot="table-row" slot-scope="props">
                    <div v-if="props.column.field === 'after'" class="td-after">
                        <Button className="--secondary --outline --mini --big-text" v-if="$store.getters.hasPermission('update sites')"
                                :onclick="()=>toggleUpdate(props.row)">
                            <font-awesome-icon :icon="['fal', 'pencil']"/>
                        </Button>
                        <Button className="--secondary --outline --mini --big-text" v-if="$store.getters.hasPermission('destroy sites')"
                                :onclick="()=>toggleDelete(props.row)">
                            <font-awesome-icon :icon="['fal', 'trash']"/>
                        </Button>
                    </div>
                    <span v-else>
                      {{ props.formattedRow[props.column.field] }}
                    </span>
                </template>
            </vue-good-table>
        </main>
    </div>
</template>

<script>
import Headbar from "../../components/headbar/Headbar";
import Search from "../../components/Search";
import Button from "../../components/Button";
import Select from "../../components/form/Select";
import ConfirmModal from "../../components/modal/ConfirmModal";
import CreateModal from "./SitesCreateModal";
import UpdateModal from "./SitesUpdateModal";
import {EventBus} from "../../EventBus";

export default {
    name: "sites-index",
    components: {ConfirmModal, Select, Button, Search, Headbar},
    data: function () {
        const columns = [
            {
                label: this.$t('sites.id'),
                field: 'id',
                sortable: false,
            },
            {
                label: this.$t('sites.name'),
                field: 'attributes.name',
                sortable: false,
            },
            {
                label: this.$t('sites.country'),
                field: 'relationships.country.attributes.name',
                sortable: false,
            },
            {
                label: this.$t('sites.groups'),
                field: row => row.relationships.site_groups.length ? row.relationships.site_groups.map(s => s.attributes.name).join(', ') : '',
                sortable: false,
            },
        ];

        if (this.$store.getters.hasAnyPermission(['update sites', 'destroy sites']))
            columns.push({
                label: this.$t('sites.actions'),
                field: 'after',
                tdClass: 'td-after',
                sortable: false
            });

        return {
            columns: columns,
            sites: [],
            is_loading_sites: false,
            totalRecords: null,
            serverParams: {
                sorting: [],
                per_page: 10
            },
            searchTerm: null,
            is_deleting: false,
            headbarExpanded: false
        }
    },
    methods: {
        toggleCreate() {
            this.$modal.show(
                CreateModal, {},
                {
                    name: 'sites-create-modal',
                    adaptive: true,
                    resizable: true,
                    height: 'auto',
                    width: 450,
                    scrollable: true,
                    classes: 'modal',
                }, {
                    'before-close': (e) => {
                        if (e.params === true)
                            this.getSites();
                    }
                }
            );
        },
        toggleUpdate(site) {
            this.$modal.show(
                UpdateModal,
                {
                    site_id: site.id,
                },
                {
                    name: 'sites-update-modal',
                    adaptive: true,
                    resizable: true,
                    height: 'auto',
                    width: 450,
                    scrollable: true,
                    classes: 'modal',
                }, {
                    'before-close': (e) => {
                        if (e.params === true)
                            this.getSites();
                    }
                }
            );
        },
        toggleDelete(site) {
            this.$modal.show(
                ConfirmModal, {
                    title: this.$t('sites.delete_site'),
                    message: this.$t('sites.prompt_delete'),
                    confirmText: this.$t('delete'),
                    cancelText: this.$t('cancel'),
                    confirmClass: '--primary',
                    cancelClass: '--secondary --outline'
                },
                {
                    name: 'confirm-modal',
                    adaptive: true,
                    resizable: true,
                    height: 'auto',
                    scrollable: true,
                    classes: 'modal',
                },
                {
                'before-close': (e) => {
                    if (e.params === true) {
                        this.is_deleting = true;
                        this.$axios.delete(`sites/${site.id}`)
                            .then(({data}) => {
                                this.is_deleting = false;
                                this.getSites();
                            })
                            .catch(e => {
                                this.is_deleting = false;

                                this.$notify({
                                    title: this.$t('error'),
                                    text: this.$larerror(e.response.data, this.$t('sites.error_delete')),
                                    type: 'error',
                                });
                            });
                        }
                    }
                }
            );
        },
        updateParams(newProps) {
            this.serverParams = Object.assign({}, this.serverParams, newProps);
        },
        onPageChange(params) {
            this.updateParams({page: params.currentPage});
            this.getSites();
        },
        onPerPageChange(params) {
            this.updateParams({per_page: params.currentPerPage});
            this.getSites();
        },
        onSortChange(params) {
            const sorts = [];

            if (!params || !params.length) {
                this.updateParams({sorting: []});
                return this.getSites();
            }

            params.forEach(p => {
                if (!p.type || p.type === 'none')
                    return;

                let sort_by = null;
                let sort_order = p.type;
                if (p.field === 'attributes.name')
                    sort_by = 'name';
                else if (p.field === 'attributes.email')
                    sort_by = 'email';
                else if (p.field === 'role')
                    sort_by = 'role';
                else
                    sort_by = p.field.split('.')[1];

                sorts.push({sort_order, sort_by})
            });

            this.updateParams({sorting: sorts});
            this.getSites();
        },
        removeParam(param) {
            this.$delete(this.serverParams, param);
        },
        search(searchTerm) {
            this.searchTerm = searchTerm;

            if (searchTerm && searchTerm.length)
                this.updateParams({term: searchTerm});
            else this.removeParam('term');

            this.getSites();
        },
        getSites() {
            this.is_loading_sites = true;

            const encodedSorting = this.serverParams.sorting.map(f => btoa(JSON.stringify(f)));

            this.$axios.get('sites', {params: {...this.serverParams, sorting: encodedSorting}})
                .then(({data}) => {
                    this.sites = data.data;
                    this.totalRecords = data.meta.total;
                    this.is_loading_sites = false;
                })
                .catch(e => {
                    this.is_loading_sites = false;

                    this.$notify({
                        title: this.$t('error'),
                        text: this.$larerror(e.response.data, this.$t('sites.error_retrieve')),
                        type: 'error',
                    });
                });
        }
    },
    mounted() {
        EventBus.$on('get-sites', this.getSites);
        this.getSites();
    },
    beforeDestroy() {
        EventBus.$off('get-sites');
    }
}
</script>

<style lang="scss" scoped>
.page-container {
    @apply flex flex-col;

    @screen lg {
        @apply flex-row;
    }

    main, .headbar-container {
        &:first-of-type {
            @screen lg {
                @apply pr-4;
            }

            @screen xl {
                @apply pr-6;
            }
        }

        &:last-of-type {
            @screen lg {
                @apply pl-4;
            }

            @screen xl {
                @apply pl-6;
            }
        }
    }

    .headbar-expanded-container {
        @apply mt-4 flex flex-col gap-y-4 items-center;

        @screen md {
            @apply hidden;
        }
    }

    .select-dropdown {
        @apply min-w-40;
    }

    .btn-search-mobile, .btn-filter-mobile {
        @apply block text-primary w-6;

        @screen md {
            @apply hidden;
        }

        &:active, &:focus {
            @apply outline-none;
        }
    }

    .search-desktop, .filter-desktop {
        @apply hidden;

        @screen md {
            @apply block;
        }
    }

    .search-container {
        @apply mr-0;
    }

    .filter-mobile {
        @apply max-w-xs;
    }

    .td-after {
        @apply flex flex-row;

        & > * {
            @apply mr-3;

            &:last-child {
                @apply mr-0;
            }
        }
    }
}
</style>
